import { Component, computed, effect, inject, OnInit, signal, untracked, ChangeDetectionStrategy } from '@angular/core'
import { Role, User } from 'depoto-core/src/entities'
import { BaseListing } from '../_base-listing/base-listing'
import { CoreService } from '../../services'
import { usersColumns } from '../../utils/definitions.util'
import { injectInfiniteQuery, injectQuery } from '@tanstack/angular-query-experimental'
import { UserGroupService } from '../../services/entity-services/userGroup.service'
import { UserService } from '../../services/entity-services/user.service'
import { createSubmittedFilterState } from '../../utils'

const DEFAULT_FILTERS = {
  email: '',
  enabled: 'all',
  groups: [],
}

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UsersComponent extends BaseListing implements OnInit {
  private userGroupService = inject(UserGroupService)
  private userService = inject(UserService)

  listingColumns = signal(usersColumns)
  sortProp = signal('lastName')
  sortDir = signal('asc')

  roles: Role[] = []
  enabledTypes: string[] = ['all', 'yes', 'no']

  filters = signal(structuredClone(DEFAULT_FILTERS))

  readonly queryUserGroupsOptions = injectQuery(() => ({
    queryKey: ['userGroups', 'options'],
    queryFn: async () => {
      return await this.userGroupService
        .getUserGroups({ sort: 'name', direction: 'asc' }, { id: null, name: null, roles: null })
        .then(result => result.items)
    },
  }))

  readonly queryFilters = computed(() => {
    const v = this.filters()
    const f: any = {}

    if (v.email?.length) f.email = v.email
    if (v.enabled !== 'all') f.enabled = v.enabled === 'yes'
    if (Array.isArray(v.groups) && v.groups.length) f.groups = v.groups

    return f
  })

  private readonly submittedFilters = createSubmittedFilterState<typeof DEFAULT_FILTERS, Record<string, any>>({
    saveView: filters =>
      this.saveViewSettings('users', {
        filters,
        sortProp: this.sortProp(),
        sortDir: this.sortDir(),
      }),
    refetch: () => this.queryUsers.refetch(),
  })
  readonly submittedQueryFilters = computed(() => this.submittedFilters.snapshot()?.request ?? {})

  readonly queryUsers = injectInfiniteQuery(() => {
    const snapshot = this.submittedFilters.snapshot()
    const sortProp = this.sortProp()
    const sortDir = this.sortDir()
    const requestFilters = snapshot?.request ?? {}

    return {
      enabled: this.submittedFilters.ready(),
      queryKey: ['users', 'list', { sortProp, sortDir, filters: requestFilters }],
      queryFn: ({ pageParam }) =>
        this.userService.getUsers(
          {
            page: pageParam,
            sort: sortProp,
            direction: sortDir,
            filters: requestFilters,
          },
          {
            id: null,
            email: null,
            enabled: null,
            groups: { id: null, name: null, roles: null },
            firstName: null,
            lastName: null,
          }
        ),
      initialPageParam: 1,
      getNextPageParam: result =>
        Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
          ? Number(result?.paginator?.current) + 1
          : undefined,
    }
  })

  totalItems = computed(() => this.queryUsers.data()?.pages?.[0]?.paginator?.totalCount ?? 0)
  areCoreDataLoading = computed(() => this.queryUserGroupsOptions.isFetching())

  constructor(protected core: CoreService) {
    super(core)
    effect(() => {
      this.queryUserGroupsOptions.data()
      this.queryUsers.data()
    })
    effect(() => {
      if (!this.submittedFilters.ready()) return
      const sortProp = this.sortProp()
      const sortDir = this.sortDir()
      untracked(() => {
        this.saveViewSettings('users', {
          filters: this.submittedFilters.snapshot()?.view ?? structuredClone(DEFAULT_FILTERS),
          sortProp,
          sortDir,
        })
      })
    })
  }

  async ngOnInit() {
    const view = this.loadViewSettings('users')
    this.filters.set(view?.filters || structuredClone(DEFAULT_FILTERS))
    this.sortProp.set(view?.sortProp || 'lastName')
    this.sortDir.set(view?.sortDir || 'asc')
    this.loadSorting('users')
    this.submittedFilters.commit(this.filters(), this.queryFilters())
    this.queryUsers.refetch()
  }

  setFilter<K extends keyof typeof DEFAULT_FILTERS>(key: K, value: (typeof DEFAULT_FILTERS)[K]) {
    this.filters.update(f => ({ ...f, [key]: value }))
  }

  clearFilters(): void {
    this.filters.set(structuredClone(DEFAULT_FILTERS))
    this.submitFilters()
  }

  filter() {
    this.submitFilters()
  }

  private submitFilters(): void {
    this.submittedFilters.submit(this.filters(), this.queryFilters())
  }
}
